import { Injectable } from '@angular/core';
import moment from 'moment';

import { PeriodName } from '@store';

import { DAYS_SHORT, MONTHS_SHORT } from '../common/enums';
import { Period } from '../models/period.model';
import dayjsBase from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';

@Injectable({
  providedIn: 'root',
})
export class DatesService {
  /**
   * Returns an array with last 7 day-short keys and optional today key in ascending order where first item is the oldest
   *
   * @param amountOfDays specify how many days are requried. Defaults to 7 ( 1 week )
   * @param currentDate overwrite default current date which is used as a reference point to generate days array
   * @param appendToday allow to remove today translation key from the array
   */
  private dayjsWithTimezoneInstance: typeof dayjsBase;
  private dayjsWithIsoDate: typeof dayjsBase;

  public getPreviousDayKeys(config?: {
    amountOfDays?: number;
    currentDate?: Date;
    excludeToday?: boolean;
    minDate?: string;
  }): string[] {
    const _amountOfDays = config?.amountOfDays || 7;
    const _currentDate = config?.currentDate || new Date();

    const values = [];

    if (!config?.excludeToday) {
      values.push('DATETIME.TODAY');
    }

    for (let j = 1; j <= _amountOfDays; ++j) {
      const earliestData = moment(config?.minDate);
      const date = moment(_currentDate).subtract(j, 'days');
      const isDateWithinRange = date.isSame(earliestData, 'day') || date.diff(earliestData) >= 0;

      if (!config?.minDate || isDateWithinRange) {
        values.push(
          // isoWeekday() returns 1-7 where 1 is Monday and 7 is Sunday so substract 1 to match DAYS_SHORT enum
          DAYS_SHORT[date.isoWeekday() - 1]
        );
      }
    }

    return values.reverse();
  }

  /**
   * Returns an array with last 12 month keys and current month in ascending order where first item is the oldest month
   *
   * @param amountOfMonths specify how many months are requried. Defaults to 12 ( 1 year )
   * @param currentDate overwrite default current date which is used as a reference point
   */
  public getPreviousMonthKeys(config?: {
    amountOfMonths?: number;
    currentDate?: Date;
    minDate?: string;
  }): string[] {
    const _amountOfMonths = config?.amountOfMonths || 12;
    const _currentDate = config?.currentDate || new Date();

    const values = [];

    for (let j = 0; j <= _amountOfMonths; ++j) {
      const earliestData = moment(config?.minDate);
      const date = moment(_currentDate).subtract(j, 'months');
      const isDateWithinRange = date.isSame(earliestData, 'month') || date.diff(earliestData) >= 0;

      if (!config?.minDate || isDateWithinRange) {
        values.push(
          MONTHS_SHORT[date.month()] // month() returns 0-11 where 0 is January and 11 is December
        );
      }
    }

    return values.reverse();
  }

  /**
   * Returns array with week numbers from - as a starting point -> current date minus amountOfWeeks, until current week
   *
   * @param amountOfWeeks specify how many weeks are requried. Defaults to 12 ( 3 quarters )
   * @param currentDate overwrite default current date which is used as a reference point
   */
  public getPreviousWeeks(config?: {
    amountOfWeeks?: number;
    currentDate?: Date;
    minDate?: string;
  }): string[] {
    const values = [];

    const _amountOfWeeks = config?.amountOfWeeks || 12;
    const _currentDate = config?.currentDate || new Date();
    for (let j = 0; j <= _amountOfWeeks; ++j) {
      const earliestData = moment(config?.minDate);
      const date = moment(_currentDate).subtract(j, 'weeks');
      const isDateWithinRange = date.isSame(earliestData, 'week') || date.diff(earliestData) >= 0;

      if (!config?.minDate || isDateWithinRange) {
        values.push(date.format('W'));
      }
    }

    return values.reverse();
  }

  /**
   * Returns array with year numbers from - as a starting point -> current date minus amountOfYears, until current year
   *
   * @param amountOfYears specify how many years are requried. Defaults to 2
   * @param currentDate overwrite default current date which is used as a reference point
   */
  public getPreviousYears(config?: {
    amountOfYears?: number;
    currentDate?: Date;
    minDate?: string;
  }): string[] {
    const _amountOfYears = config?.amountOfYears || 2;
    const _currentDate = config?.currentDate || new Date();
    const currentYear = _currentDate.getFullYear();

    const value = [];
    for (let i = currentYear - _amountOfYears; i <= currentYear; ++i) {
      const earliestData = moment(config?.minDate);
      const date = moment(i.toString());
      const isDateWithinRange = date.isSame(earliestData, 'year') || date.diff(earliestData) >= 0;

      if (!config?.minDate || isDateWithinRange) {
        value.push(i.toString());
      }
    }

    return value;
  }

  /**
   * Generate well formatted string for past period of time in a desired format. Example: "2003-01-01 - 2019-12-31"
   *
   * @param durationInput A base durations which this function supports are 'day' | 'week' | 'month' | 'year'
   * @param periodStartSubtractAmount An amount of units of time in the given period which will be subtracted from start date
   * @param dateFormat A string accepted by moment.format(), default value is 'YYYY-MM-DD'. Try 'L' to get fate in local format
   * @param periodEndSubtractAmount An amount of units of time in the given period which will be subtracted from end date
   */
  public getPeriod(
    durationInput: PeriodName,
    periodStartSubtractAmount: number,
    dateFormat = 'YYYY-MM-DD',
    periodEndSubtractAmount = 1
  ): Period | null {
    const duration = durationInput as moment.unitOfTime.Base;

    if (
      periodStartSubtractAmount <= 0 ||
      periodEndSubtractAmount <= 0 ||
      periodStartSubtractAmount < periodEndSubtractAmount
    ) {
      return null;
    }
    const periodStart = moment().subtract(periodStartSubtractAmount, duration);
    const periodEnd = moment().subtract(periodEndSubtractAmount, duration);

    switch (duration) {
      case 'week': {
        return {
          start: periodStart.startOf('isoWeek').format(dateFormat),
          end: periodEnd.endOf('isoWeek').format(dateFormat),
        };
      }
      case 'day':
      case 'month':
      case 'year': {
        return {
          start: periodStart.startOf(durationInput).format(dateFormat),
          end: periodEnd.endOf(durationInput).format(dateFormat),
        };
      }
      default:
        return null;
    }
  }

  public getMomentFormattedDateForSpecificTimezone(date: string, timeZone: string): string {
    return moment(date).tz(timeZone).format('YYYY-MM-DDTHH:mm:ss');
  }

  public getMomentFormattedDateForTotalMinutes(totalMinutes: number): string {
    let date = moment().utcOffset(0);

    date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    date.add(totalMinutes, 'minutes');

    return date.format('HH:mm');
  }

  getDayjsWithTimezone(): typeof dayjsBase {
    if (this.dayjsWithTimezoneInstance) {
      return this.dayjsWithTimezoneInstance;
    } else {
      this.dayjsWithTimezoneInstance = dayjsBase;
      this.dayjsWithTimezoneInstance.extend(utc);
      this.dayjsWithTimezoneInstance.extend(timezone);
      this.dayjsWithTimezoneInstance.extend(customParseFormat);

      return this.dayjsWithTimezoneInstance;
    }
  }

  getDayjsWithIsoDate(): typeof dayjsBase {
    if (this.dayjsWithIsoDate) {
      return this.dayjsWithIsoDate;
    } else {
      this.dayjsWithIsoDate = dayjsBase;
      this.dayjsWithIsoDate.extend(isoWeek);
      this.dayjsWithIsoDate.extend(isoWeeksInYear);
      this.dayjsWithIsoDate.extend(isLeapYear);

      return this.dayjsWithIsoDate;
    }
  }
}
